import React, { useCallback, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { useHistory, useLocation } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'
import Amplitude from '../../utils/amplitudeHelper'
import routes from '../../routes'
import config from '../../config'
import { showGlobalModal } from 'src/ducks/layout'
import { GlobalModalTypes } from 'src/components/GlobalModals/modalTypes'
import { ATTENDANCE_NOTICE_FLAG } from 'src/ducks/auth'
import { mobileAssets } from 'src/containers/PreCache'

import plenaryIcon from 'src/images/ntc/lobby/lobby-card-plenary-hall.svg'
import programmeIcon from 'src/images/ntc/lobby/lobby-card-programme.svg'
import speakerIcon from 'src/images/ntc/lobby/lobby-card-speaker.svg'
import galleryIcon from 'src/images/ntc/lobby/lobby-card-gallery.svg'
import helpDeskIcon from 'src/images/ntc/lobby/lobby-card-help-desk.svg'
import faqIcon from 'src/images/ntc/lobby/lobby-card-faq.svg'
import chevronIcon from 'src/images/ntc/lobby/lobby-carousel-chevron-right.svg'

const CARD_STEP = 318

const MobileLobby = ({ currentUser }) => {
  const scrollRef = useRef(null)
  const history = useHistory()
  const dispatch = useDispatch()
  const { search } = useLocation()
  const [scrollState, setScrollState] = useState({ canScrollLeft: false, canScrollRight: true })

  const updateScrollState = useCallback(() => {
    const scrollContainer = scrollRef.current
    if (!scrollContainer) return

    const maxScrollLeft = scrollContainer.scrollWidth - scrollContainer.clientWidth
    setScrollState({
      canScrollLeft: scrollContainer.scrollLeft > 1,
      canScrollRight: scrollContainer.scrollLeft < maxScrollLeft - 1,
    })
  }, [])

  useEffect(() => {
    const toastMsg = new URLSearchParams(search).get('message')
    if (toastMsg) toast(toastMsg, { toastId: 'auditoriumClosed' })
  }, [search])

  useEffect(() => {
    document.title = `${config.eventName} | Lobby`
    Amplitude.visit('Lobby')
  }, [currentUser.id])

  // Attendance reminder — fires once per login, on the first Lobby landing.
  useEffect(() => {
    if (sessionStorage.getItem(ATTENDANCE_NOTICE_FLAG)) {
      sessionStorage.removeItem(ATTENDANCE_NOTICE_FLAG)
      dispatch(showGlobalModal(GlobalModalTypes.AttendanceNotice))
    }
  }, [dispatch])

  useEffect(() => {
    updateScrollState()
    window.addEventListener('resize', updateScrollState)
    return () => window.removeEventListener('resize', updateScrollState)
  }, [updateScrollState])

  const showHelpDesk = () => {
    const cpt = window.chaport
    if (!cpt) return

    cpt.on('ready', function () {
      cpt.setVisitorData({
        name: currentUser.fullName,
        email: currentUser.email,
      })
      cpt.open()
    })
    cpt.on('collapsed', function () {
      cpt.close()
    })
    Amplitude.clickHelpDesk()
  }

  const scrollCards = (direction) => {
    if (!scrollRef.current) return
    scrollRef.current.scrollBy({ left: direction * CARD_STEP, behavior: 'smooth' })
  }

  const navigateTo = (route, external = false) => {
    if (!route) return
    if (external) {
      window.open(route, '_blank', 'noopener,noreferrer')
      return
    }
    history.push(route)
  }

  const menuItems = [
    {
      label: 'Plenary Hall',
      icon: plenaryIcon,
      iconClass: 'plenary-icon',
      onClick: () => navigateTo(routes.auditorium),
    },
    {
      label: 'Programme',
      icon: programmeIcon,
      iconClass: 'programme-icon',
      onClick: () => dispatch(showGlobalModal(GlobalModalTypes.Agenda)),
    },
    {
      label: 'Speaker',
      icon: speakerIcon,
      iconClass: 'speaker-icon',
      onClick: () => dispatch(showGlobalModal(GlobalModalTypes.Speakers)),
    },
    {
      label: 'Gallery',
      icon: galleryIcon,
      iconClass: 'gallery-icon',
      onClick: () => navigateTo(routes.onDemand),
    },
    {
      label: 'Help Desk',
      icon: helpDeskIcon,
      iconClass: 'help-icon',
      onClick: showHelpDesk,
    },
    {
      label: 'FAQ',
      icon: faqIcon,
      iconClass: 'faq-icon',
      onClick: () => navigateTo(config.FAQ_PDF_URL, true),
    },
  ]

  return (
    <Page>
      <div className="wrap">
        <img className="masthead" src={mobileAssets.lobbyTitle} alt="National Tax Conference 2026" />

        <section className="hall-panel" aria-labelledby="virtual-hall-title">
          <h1 id="virtual-hall-title">Virtual Hall</h1>

          <div className="list-wrapper" ref={scrollRef} onScroll={updateScrollState}>
            {menuItems.map((item) => (
              <button className="item-box" type="button" onClick={item.onClick} key={item.label}>
                <span className="icon-wrapper" aria-hidden="true">
                  <img className={item.iconClass} src={item.icon} alt="" />
                </span>
                <span className="item-label">{item.label}</span>
              </button>
            ))}
          </div>

          <div className="lobby-controls">
            <button
              className="scroll-button scroll-left"
              type="button"
              onClick={() => scrollCards(-1)}
              disabled={!scrollState.canScrollLeft}
              aria-label="Previous lobby options"
            >
              <img src={chevronIcon} alt="" />
            </button>
            <button
              className="scroll-button"
              type="button"
              onClick={() => scrollCards(1)}
              disabled={!scrollState.canScrollRight}
              aria-label="Next lobby options"
            >
              <img src={chevronIcon} alt="" />
            </button>
          </div>
        </section>
      </div>
    </Page>
  )
}

const Page = styled.main`
  --dark-blue: #262c65;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
  font-family: 'Din', 'DIN', sans-serif;

  .wrap {
    position: relative;
    width: 100%;
    height: 100%;
    overflow: hidden;
    background: url(${mobileAssets.dashboardBg}) center / cover no-repeat;
  }

  .masthead {
    position: absolute;
    z-index: 1;
    top: 54px;
    left: 20px;
    width: min(calc(100% - 40px), 356px);
    height: auto;
    display: block;
  }

  .hall-panel {
    position: absolute;
    z-index: 2;
    left: 0;
    right: 0;
    bottom: 0;
    height: 449px;
    max-height: calc(100vh - 150px);
    max-height: calc(100dvh - 150px);
    overflow: hidden;
    border-radius: 20px 20px 0 0;
    background: linear-gradient(180deg, rgba(0, 11, 35, 0) 0%, #000b23 100%);
    backdrop-filter: blur(4.85px);
  }

  h1 {
    position: absolute;
    top: 30px;
    left: 20px;
    right: 20px;
    margin: 0;
    color: #fff;
    font-size: 30px;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    text-transform: uppercase;
  }

  .list-wrapper {
    position: absolute;
    top: 91px;
    left: 0;
    width: 100%;
    height: 191px;
    box-sizing: border-box;
    display: flex;
    gap: 15px;
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0 20px;
    scroll-padding-left: 20px;
    scroll-snap-type: x mandatory;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .item-box {
    flex: 0 0 144px;
    width: 144px;
    height: 191px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
    padding: 37px 15px;
    border: 1px solid #fff;
    border-radius: 15px;
    background:
      linear-gradient(180deg, rgba(255, 255, 255, 0.75) 0%, rgba(255, 238, 189, 0.75) 100%),
      linear-gradient(90deg, #fff 0%, #fff 100%);
    color: var(--dark-blue);
    font: inherit;
    cursor: pointer;
    scroll-snap-align: start;
    -webkit-tap-highlight-color: transparent;
  }

  .item-box:focus-visible,
  .scroll-button:focus-visible {
    outline: 2px solid #fff;
    outline-offset: -4px;
  }

  .item-box:active {
    transform: translateY(1px);
  }

  .icon-wrapper {
    width: 53px;
    height: 53px;
    flex: 0 0 53px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .plenary-icon {
    width: 34.5px;
    height: 41.5px;
  }

  .programme-icon,
  .help-icon,
  .faq-icon {
    width: 40px;
    height: 40px;
  }

  .speaker-icon {
    width: 40px;
    height: 37px;
  }

  .gallery-icon {
    width: 34px;
    height: 27px;
  }

  .item-label {
    width: 114px;
    color: var(--dark-blue);
    font-size: 18px;
    font-weight: 700;
    line-height: 1;
    text-align: center;
    text-transform: uppercase;
  }

  .lobby-controls {
    position: absolute;
    top: 307px;
    left: 0;
    right: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 32px;
  }

  .scroll-button {
    width: 40px;
    height: 40px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px;
    border: 0;
    border-radius: 10px;
    background: var(--Gold-Gradient-01);
    box-shadow: 0 4px 6.15px rgba(0, 0, 0, 0.21);
    cursor: pointer;
  }

  .scroll-button:disabled {
    opacity: 0.5;
    cursor: default;
  }

  .scroll-button img {
    width: 9px;
    height: 16.5px;
  }

  .scroll-left img {
    transform: rotate(180deg);
  }

  @media (max-height: 650px) {
    .masthead {
      top: 24px;
      width: min(calc(100% - 80px), 320px);
    }

    .hall-panel {
      transform: scale(0.9);
      transform-origin: bottom center;
      width: 111.12%;
      right: auto;
    }
  }
`

export default MobileLobby
